import * as React from 'react';
import Button from '@mui/material/Button';
import { useCreateAlert } from 'lib/alert/hooks/useCreateAlert';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { convertFilterOperatorForNullValue } from 'helpers/filterHelper';
import CircularProgress from '@mui/material/CircularProgress';
import { useUpdateAlert } from 'lib/alert/hooks/useUpdateAlert';
import { useDispatch } from 'store/hooks';
import { goToGuideNextStep, trackEvent } from 'helpers/trackerHelpers';
import { useAlertForm } from '../Contexts/AlertFormContext';
import { AlertAction, useAlertAction } from '../Contexts/AlertActionContext';
import { useModalContext } from '../Contexts/ModalContext';
import { Alert } from 'lib/alert/models';

type Props = {
    disabled?: boolean;
    chartKey: number;
    action: AlertAction;
};

const SubmitButton = ({
    disabled = false,
    chartKey,
    action,
}: Props): JSX.Element => {
    const { alert } = useAlertForm();
    const { setAction } = useAlertAction();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { setDirty } = useModalContext();

    const onSuccess = () => {
        queryClient.invalidateQueries({
            queryKey: ['chart', chartKey, 'alerts', 'owned'],
        });
        setAction({ name: 'list' });
        setDirty(false);
        goToGuideNextStep();
    };

    const onUpdateSuccess = (message: string) => {
        onSuccess();
        queryClient.invalidateQueries({
            queryKey: ['alert', action.alertId],
        });
        queryClient.invalidateQueries({
            queryKey: ['alertRecords', action.alertId],
        });
        dispatch.vision.notifyUser({
            message,
            snackType: 'success',
        });
    };

    const { mutate: createAlert, isPending: isCreatingAlert } = useCreateAlert(
        'chart',
        chartKey,
        onSuccess,
    );

    const { mutate: updateAlert, isPending: isUpdatingAlert } = useUpdateAlert(
        action.alertId,
        onUpdateSuccess,
    );

    const isLoading = isCreatingAlert || isUpdatingAlert;

    const alertAction = () => {
        const { metric, filters, ...alertRest } = alert;
        const alertObject: Partial<Alert> = {
            ...alertRest,
            metric: {
                field: metric!.field,
                aggregation: metric!.aggregation,
                type: metric?.type,
            },
            // @ts-expect-error This is hard to type
            filters: filters!.map((f) =>
                convertFilterOperatorForNullValue({
                    field: f.field,
                    operation: f.operation,
                    value: f.value,
                    valueScale: f.valueScale,
                    displayIndex: f.displayIndex,
                }),
            ),
        };
        if (action.name === 'create') {
            createAlert(alertObject);
            trackEvent('decision/alerts/created');
        } else {
            updateAlert(alertObject);
            trackEvent('decision/alerts/updated');
        }
    };

    const getActionLabel = (): string => {
        if (isLoading) {
            return t(`alert.${action.name}.loading`);
        }
        if (action.name === 'create') {
            return t('alert.validate');
        }
        return t('alert.edit.title');
    };

    return (
        <Button
            variant="contained"
            onClick={alertAction}
            disabled={disabled || isLoading}
            sx={{ mt: 2 }}
            startIcon={
                isLoading && (
                    <CircularProgress sx={{ color: 'white' }} size={20} />
                )
            }
        >
            {getActionLabel()}
        </Button>
    );
};

export default SubmitButton;
